/** @format */

import { FC, useEffect, useState, useCallback } from "react";
import { track } from "../../lib/posthog";
import { useTrackChangesState } from "../../store/useTrackChangesStore";
import type { SopListFilters } from "../../store/useSopStore";
import Header from "../../components/ui/Header";
import HeroSection from "../../components/ui/HeroSection";
import { NoOrganizationState } from "../../components/NoOrganizationState";
import { useOrganizationStore } from "../../store/useOrganizationStore";
import { useSuperAdmin } from "../../hooks/useSuperAdmin";
import { useOrgRole } from "../../hooks/useOrgRole";
import "../../components/ui/Header.css";
import "./SopDashBoard.css";
import SopList from "./SopList";
import SOPListFilters, { countFilters } from "../../components/sop/SOPListFilters";

const DEFAULT_PAGE_SIZE = 10;

const SopDashBoard: FC = () => {
  const { fetchSOPs, error, clearError, isLoading } = useTrackChangesState();
  const { organizations } = useOrganizationStore();
  const { isSuperAdmin } = useSuperAdmin();
  const { canEdit, canAccessRMA } = useOrgRole();
  const [appliedFilters, setAppliedFilters] = useState<SopListFilters>({});

  useEffect(() => {
    fetchSOPs({ page: 1, limit: DEFAULT_PAGE_SIZE });
  }, [fetchSOPs]);

  useEffect(() => {
    track("dashboard_viewed", { tab_name: "sop_list" });
  }, []);

  const filtersForApi = useCallback(
    (f: SopListFilters) =>
      isSuperAdmin ? f : { ...f, organizationId: undefined },
    [isSuperAdmin],
  );

  const handleApplyFilters = useCallback(
    (filters: SopListFilters) => {
      setAppliedFilters(filters);
      clearError();
      fetchSOPs({
        page: 1,
        limit: DEFAULT_PAGE_SIZE,
        ...filtersForApi(filters),
      });
    },
    [fetchSOPs, clearError, filtersForApi],
  );

  const handleClearFilters = useCallback(() => {
    setAppliedFilters({});
    clearError();
    fetchSOPs({ page: 1, limit: DEFAULT_PAGE_SIZE });
  }, [fetchSOPs, clearError]);

  const appliedFiltersForList = isSuperAdmin
    ? appliedFilters
    : { ...appliedFilters, organizationId: undefined };

  const showNoOrgState = organizations.length === 0 && !isSuperAdmin;

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-black text-white">
      <Header />
      <main className="pt-20 px-6 lg:px-10">
        <div className="pt-8 w-full mx-auto">
          <div className="max-w-[1440px] mx-auto pb-8">
            {showNoOrgState ? (
              <NoOrganizationState />
            ) : (
              <>
                <HeroSection
                  title="Standard Operating Procedures"
                  description="Manage your manufacturing processes with AI-powered documentation."
                />

                <div className="bg-gradient-to-br from-gray-900/20 to-black/40 backdrop-blur-xl border border-gray-800/15 rounded-2xl shadow-2xl shadow-black/50">
                  <div className="space-y-3">
                    <SOPListFilters
                      organizations={organizations}
                      onApply={handleApplyFilters}
                      onClear={handleClearFilters}
                      activeFilterCount={countFilters(appliedFiltersForList)}
                      disabled={isLoading}
                      showOrganizationFilter={isSuperAdmin}
                    />
                    {error && (
                      <div
                        className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-300 flex items-center justify-between"
                        role="alert"
                      >
                        <span>{error}</span>
                        <button
                          type="button"
                          onClick={clearError}
                          className="ml-2 underline hover:no-underline"
                        >
                          Dismiss
                        </button>
                      </div>
                    )}
                    <SopList
                      canEdit={canEdit}
                      canAccessRMA={canAccessRMA}
                      isSuperAdmin={isSuperAdmin}
                      appliedFilters={appliedFiltersForList}
                      pageSize={DEFAULT_PAGE_SIZE}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SopDashBoard;
